import { Link } from 'react-router-dom';
import { computeEventReadiness, type Event, type EventOverview } from '@peles/shared';
import { EventLogo } from './EventLogo';
import { EventCountdown } from './EventCountdown';
import { EventStatusBadge } from './EventStatusBadge';
import { ReadinessRing } from './ReadinessRing';
import { formatShortDate } from '../../utils/eventDates';

function compactMoney(amount: number): string {
  if (Math.abs(amount) >= 1000) return `$${(amount / 1000).toFixed(amount >= 100_000 ? 0 : 1)}k`;
  return `$${Math.round(amount)}`;
}

interface EventCardProps {
  event: Event;
  /** Numbers for the footer and readiness ring; a skeleton line while loading. */
  overview?: EventOverview;
  /** Board cards: no footer, smaller logo. */
  compact?: boolean;
}

/**
 * One event on the list's Cards and Board views: logo, name, date and venue,
 * countdown, status, readiness, and (full cards) guests / expenses / what the
 * client still owes.
 */
export function EventCard({ event, overview, compact = false }: EventCardProps) {
  const readiness = overview ? computeEventReadiness(overview) : null;
  const where = [event.startAt && formatShortDate(event.startAt), event.venueName]
    .filter(Boolean)
    .join(' · ');
  return (
    <Link
      to={`/events/${event.id}`}
      className={`card-lift group block bg-white rounded-2xl border border-gray-200 hover:border-primary-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 ${
        compact ? 'p-3' : 'p-4'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Board columns are narrow; the name needs the room more than the logo. */}
        {!compact && (
          <EventLogo eventId={event.id} eventLogoUrl={event.logoUrl} sizeClassName="w-12 h-12" />
        )}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 truncate group-hover:text-primary-700">
            {event.name}
          </p>
          <p className="text-xs text-gray-500 mt-0.5 truncate">
            {where || '—'}
            {event.eventTypeName && !compact && (
              <span className="text-primary-700"> · {event.eventTypeName}</span>
            )}
          </p>
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            <EventCountdown startAt={event.startAt} />
            {!compact && <EventStatusBadge status={event.status} />}
          </div>
        </div>
        {!compact && <ReadinessRing score={readiness?.score ?? null} size={46} />}
      </div>
      {!compact && (
        <dl className="grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-gray-100">
          {overview ? (
            <>
              <div>
                <dt className="text-[10px] uppercase tracking-wider text-gray-500">Guests</dt>
                <dd className="text-sm font-bold text-gray-900">
                  {overview.guests.confirmed}/{overview.guests.total}
                </dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-wider text-gray-500">Expenses</dt>
                <dd className="text-sm font-bold text-gray-900">
                  {compactMoney(overview.money.totalCost)}
                </dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-wider text-gray-500">Client owes</dt>
                <dd
                  className={`text-sm font-bold ${
                    overview.money.clientBalanceDue > 0.005 ? 'text-red-700' : 'text-green-700'
                  }`}
                >
                  {compactMoney(overview.money.clientBalanceDue)}
                </dd>
              </div>
            </>
          ) : (
            [0, 1, 2].map((i) => (
              <div key={i} className="space-y-1.5 animate-pulse" aria-hidden>
                <div className="h-2 w-10 bg-gray-100 rounded" />
                <div className="h-3.5 w-14 bg-gray-100 rounded" />
              </div>
            ))
          )}
        </dl>
      )}
      {compact && readiness?.score != null && (
        <div className="mt-2 flex items-center justify-between text-[11px] text-gray-500">
          <span>Ready</span>
          <span className="font-semibold text-gray-800">{readiness.score}%</span>
        </div>
      )}
    </Link>
  );
}
